import { useMemo, useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Search, Loader2 } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const copyable = (u) => u.role !== "admin" && u.email !== "admin@taskflow.com";

const PersonRow = ({ u, checked, onToggle, radio, testid }) => (
  <label className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-sm cursor-pointer ${checked ? "border-indigo-400 bg-indigo-50" : "border-slate-200 bg-white"}`} data-testid={testid}>
    {radio ? <input type="radio" checked={checked} onChange={onToggle} className="h-4 w-4 accent-indigo-600" />
      : <Checkbox checked={checked} onCheckedChange={onToggle} />}
    <span className="min-w-0 flex-1"><span className="block truncate font-medium text-slate-800">{u.name}</span>
      <span className="block truncate text-[11px] text-slate-500">{u.email} · {u.team || "—"}</span></span>
  </label>
);

/* Copy one member's access onto one or many members (admin). */
export const CopyAccessDialog = ({ open, onClose, users, source: src0, targets: tg0, onDone }) => {
  const [source, setSource] = useState(null);
  const [targets, setTargets] = useState(new Set());
  const [flags, setFlags] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setSource(src0 || null); setTargets(new Set(tg0 || [])); setFlags(false); setQ(""); } }, [open, src0, tg0]);
  const list = useMemo(() => {
    const k = q.trim().toLowerCase();
    return users.filter(u => !k || `${u.name} ${u.email} ${u.team}`.toLowerCase().includes(k)).slice(0, 80);
  }, [users, q]);
  const pickingSource = !source;
  const ok = useMemo(() => new Set(users.filter(u => copyable(u) && u.id !== source?.id).map(u => u.id)), [users, source]);
  const sendIds = [...targets].filter(id => ok.has(id));
  const toggle = (id) => setTargets(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const go = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/users/copy-access`,
        { source_id: source.id, target_ids: sendIds, include_flags: flags }, { headers: getAuthHeader() });
      toast.success(`Copied ${source.name}'s access to ${r.data.copied} member${r.data.copied === 1 ? "" : "s"}${r.data.skipped ? ` (${r.data.skipped} admin skipped)` : ""}`);
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Copy failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="copy-access-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Copy className="h-5 w-5 text-indigo-600" /> Copy access</DialogTitle>
          <DialogDescription>{pickingSource ? "Step 1 — pick the member to copy FROM." : <>From <b>{source.name}</b> — tick who gets the same access.</>}</DialogDescription>
        </DialogHeader>
        {source && (
          <div className="flex items-center justify-between rounded-lg bg-indigo-50 px-3 py-2 text-xs" data-testid="copy-access-source">
            <span className="truncate">Copy from: <b>{source.name}</b> ({source.email})</span>
            <button type="button" className="font-semibold text-indigo-700 underline" onClick={() => setSource(null)} data-testid="copy-access-change-source">Change</button>
          </div>)}
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, email, team…" data-testid="copy-access-search"
            className="h-9 w-full rounded-md border pl-8 pr-2 text-sm" />
        </div>
        <div className="space-y-1.5" data-testid="copy-access-list">
          {list.filter(u => pickingSource || (u.id !== source.id && copyable(u))).map(u => (
            <PersonRow key={u.id} u={u} radio={pickingSource} testid={`copy-access-${pickingSource ? "src" : "tgt"}-${u.id}`}
              checked={pickingSource ? false : targets.has(u.id)}
              onToggle={() => pickingSource ? setSource(u) : toggle(u.id)} />))}
        </div>
        {!pickingSource && (
          <div className="sticky bottom-[-1.5rem] -mx-6 -mb-6 space-y-2 border-t bg-white px-6 py-3 max-sm:bottom-[-1rem] max-sm:-mx-4 max-sm:-mb-4 max-sm:px-4">
            <label className="flex items-start gap-2 text-xs text-slate-700">
              <Checkbox checked={flags} onCheckedChange={(v) => setFlags(!!v)} data-testid="copy-access-flags" />
              <span>Also copy special ticks (Query Team, PP User, Pending-by-Service report)</span>
            </label>
            <p className="text-[11px] text-slate-500">Copies panels, sub-panels, View/Edit rights, Order Register services, countries and view services. Replaces what the members had.</p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
              <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy || !sendIds.length} onClick={go} data-testid="copy-access-confirm">
                {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Copy className="mr-1 h-4 w-4" />} Copy to {sendIds.length || ""} member{sendIds.length === 1 ? "" : "s"}
              </Button>
            </div>
          </div>)}
      </DialogContent>
    </Dialog>
  );
};
